// Latin binomial naming for one real fired neuron (brief D10).
//
// Genus maps to the neuron's REAL BANC v888 annotation (region + cell_class);
// the species epithet encodes side (sinistr-/dextr-/incert- stems) and is
// picked from a per-side pool by keccak(stateRoot, neuronId) + flow index.
// Everything here is a pure function of the on-chain thought + annotations.
import { AbiCoder, keccak256 } from 'ethers'

const coder = AbiCoder.defaultAbiCoder()

const REGION_GENUS = {
  central_brain: 'Cerebralis',
  optic_lobe: 'Optica',
  ventral_nerve_cord: 'Ventralis',
  unknown: 'Ignota',
}

// cell_class refinements that name real anatomy (olfactory ORNs innervate the
// antennal lobe); anything unmapped stays regional.
const CLASS_GENUS = {
  olfactory_receptor_neuron: 'Antennalis',
}

const REGION_CODE = {
  central_brain: 'CB',
  optic_lobe: 'OL',
  ventral_nerve_cord: 'VNC',
  unknown: 'UN',
}

// Per-side epithet pools (feminine, agreeing with the -a/-is genus forms).
// flow rotates the pick so side + flow + stateRoot hash all shape the epithet.
const EPITHETS = {
  left: [
    'sinistra', 'sinistralis', 'sinistrata', 'sinistrans', 'sinistrella',
    'sinistrina', 'sinistrora', 'sinistrula', 'sinistranda', 'sinistravis',
    'sinistrosa', 'sinistruosa', 'sinistraxis', 'sinistromena', 'sinistrophila', 'sinistramara',
  ],
  right: [
    'dextra', 'dextralis', 'dextrata', 'dextrans', 'dextrella',
    'dextrina', 'dextrora', 'dextrula', 'dextranda', 'dextravis',
    'dextrosa', 'dextruosa', 'dextraxis', 'dextromena', 'dextrophila', 'dextramara',
  ],
  unknown: [
    'incerta', 'incertalis', 'incertata', 'incertans', 'incertella',
    'incertina', 'incertora', 'incertula', 'incertanda', 'incertavis',
    'incertosa', 'incertuosa', 'incertaxis', 'incertomena', 'incertophila', 'incertamara',
  ],
}

const FLOWS = ['intrinsic', 'afferent', 'efferent', 'unknown']

export function regionCode(region) {
  return REGION_CODE[region] ?? REGION_CODE.unknown
}

export function genusName({ region, cell_class }) {
  return CLASS_GENUS[cell_class] ?? REGION_GENUS[region] ?? REGION_GENUS.unknown
}

export function speciesEpithet({ stateRoot, neuronId, side, flow }) {
  const pool = EPITHETS[side] ?? EPITHETS.unknown
  const flowShift = FLOWS.indexOf(flow) < 0 ? 0 : FLOWS.indexOf(flow)
  const h = BigInt(keccak256(coder.encode(['bytes32', 'uint256'], [stateRoot, BigInt(neuronId)])))
  return pool[(Number(h % 9973n) + flowShift * 5) % pool.length]
}

export function binomial(genome) {
  const name = `${genusName(genome)} ${speciesEpithet(genome)}`
  if (name.length > 50) throw new Error(`binomial "${name}" exceeds o1's 50-char cap`)
  return name
}

export function symbol({ region, neuronId }) {
  const s = `${regionCode(region)}${Number(neuronId)}`
  if (s.length > 11) throw new Error(`symbol "${s}" exceeds o1's 11-char cap`)
  if (/\s/.test(s)) throw new Error('o1 symbols allow no whitespace')
  return s
}

/** Parse one neuron's annotation from the in-memory verified tables. */
export function annotationFromTables(bin, doc, neuronId) {
  const off = Number(neuronId) * 5
  if (off + 5 > bin.length) throw new Error(`neuronId ${neuronId} outside the kernel (${bin.length / 5} slots)`)
  const superId = bin[off]
  const classId = bin[off + 1]
  const typeId = (bin[off + 2] << 8) | bin[off + 3]
  const flags = bin[off + 4]
  return {
    super_class: doc.tables.super_class[superId],
    cell_class: doc.tables.cell_class[classId],
    cell_type: doc.tables.cell_type[typeId],
    region: doc.region[flags & 3],
    side: doc.side[(flags >> 2) & 1],
    flow: doc.flow[flags >> 3],
  }
}
